/**
 * Auth utilities — thin adapter over Zustand authStore.
 *
 * Wave 14d: Consolidated to use the Zustand auth store as the single
 * source of truth. All getters read from the store; all setters write
 * to the store. The `persist` middleware handles localStorage serialization
 * under the `auth-storage` key automatically.
 *
 * Token storage:
 * - Production: httpOnly cookie set by backend (frontend can't read it)
 * - Migration: client-visible token in Zustand store for Authorization header
 */

import { useAuthStore } from '../stores/authStore';
import type { AppRoute, UserRole } from '../api/dtos/user.dto';

/** Read token from Zustand store (persisted to localStorage automatically). */
export function getToken(): string | null {
  return useAuthStore.getState().token;
}

/** Write token to Zustand store. */
export function setToken(token: string): void {
  useAuthStore.getState().updateToken(token);
}

/** Clear all auth state via Zustand store (also cleans up legacy keys). */
export function clearAuth(): void {
  useAuthStore.getState().clearAuth();
}

/** Read userId from Zustand store. */
export function getUserId(): string | null {
  return useAuthStore.getState().userId;
}

/** Write userId — performs a partial update via setAuth. */
export function setUserId(id: string): void {
  const state = useAuthStore.getState();
  useAuthStore.getState().setAuth(
    state.token || '',
    id,
    state.username || id,
    state.userRole || ('user' as UserRole),
    state.allowedRoutes,
  );
}

/** Write userRole. */
export function setUserRole(role: string): void {
  useAuthStore.getState().updateUserRole(role as UserRole);
}

/** Write allowedRoutes. */
export function setAllowedRoutes(routes: string[]): void {
  useAuthStore.getState().updateAllowedRoutes(routes as AppRoute[]);
}

/** Read userRole from Zustand store. */
export function getUserRole(): string | null {
  return useAuthStore.getState().userRole;
}

/** Read allowedRoutes from Zustand store. */
export function getAllowedRoutes(): string[] {
  return useAuthStore.getState().allowedRoutes;
}

/** Check auth status — derived from token/userId. */
export function isAuthenticated(): boolean {
  const state = useAuthStore.getState();
  return !!(state.token || state.userId);
}
